import axios from '@/lib/axios'
import { useRolesStore } from '@/stores/roles'
import { UseQueryOptions, useQuery } from '@tanstack/vue-query'
import { on } from 'events'
import { set } from 'lodash'
import { storeToRefs } from 'pinia'
import { onBeforeMount, onMounted, ref, watch } from 'vue'

import useAuthUser from '@/composables/useAuthUser'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import useCompanies from '@/modules/companies/composables/useCompanies'

export interface Role {
	id: number
	name: string
	title: string
	companyId: number
	usersCount: number
	companyName?: string
}

interface Params {
	companyId?: number | undefined
	immediate?: boolean
}

const getRolesCompany = async (
	params: Params,
	errorHandling = false
): Promise<Role[]> => {
	const { data } = await axios.get<Role[]>(
		`api/companies/${params.companyId}/roles`,
		{ errorHandling } as CustomAxiosRequestConfig
	)

	return data
}

const useRoles = (props: Params, errorHandling?: boolean) => {
	const { immediate = true } = props

	const { setCompanyId } = useRolesStore()
	const { roles, companyId } = storeToRefs(useRolesStore())

	const { data, error, isLoading, refetch } = useQuery(
		['rolesCompany', companyId],
		() => {
			return getRolesCompany(
				{
					companyId: companyId.value || props.companyId,
				},
				errorHandling
			)
		},
		{
			enabled: immediate,
			retry: false,
			refetchOnWindowFocus: false,
		}
	)

	watch(
		companyId,
		(value) => {
			refetch()
		},
		{
			immediate,
		}
	)

	// Watch para actualizar el store cuando lleguen nuevos datos
	watch(
		data,
		(newData) => {
			if (newData) {
				roles.value = newData
			}
		},
		{
			immediate: true,
		}
	)

	return {
		roles,
		error,
		isLoading,
		data,
		refetch,
		companyId,
		setCompanyId,
	}
}

export default useRoles
